import {
  useCreatePeriodVariantMutation,
  useGetPeriodVariantByIdMutation,
  useGetPeriodVariantsMutation,
  useGetRuleTypesMutation,
  useGetRulesMutation,
  useGetRulesVariablesMutation,
  useUpdatePeriodVariantMutation,
  useUpdatePeriodVariantStateMutation,
} from "src/services/selectionDatesServices";
import {
  MediumLargeHeadingStyled,
  SectionHeaderStyled,
  SmallHeadingStyled,
} from "../../../components/custom/texts/texts.styled";
import { Button, Card, Drawer, Dropdown, Flex, Form, Icon } from "@nubeteck/components";
import { Collapse, Input, InputNumber, Layout, Modal, Select, Table, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { FormInstance } from "antd/lib/form";
import { useErrorSavingDataToast, useSuccessRegisterToast } from "src/constants";
import ConfigurationInput from "../generalSettings/settingsInput";
import { IPeriodosFechas, IReglasVariantes } from "@/Interfaces/datesSettings";
import { ArrayUtils } from "@nubeteck/utils";
import { getImportDataPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { _translateValue } from "../translation-gestor/translateValue";

const { Content } = Layout;
const SCREEN_NAME = "PantallaPeriodEstimationVariants";

const PeriodEstimationVariants = ({
  goBack,
  periodo,
}: {
  goBack: () => void;
  periodo: IPeriodosFechas;
}) => {
  const [form] = Form.useForm<IReglasVariantes>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [varianteId, setVarianteId] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalVariablesOpen, setModalvariablesOpen] = useState(false);
  const [variableSelected, setVariableSelected] = useState<string | null>(null);
  const { locale } = useLocale();
  const [importDataPage, setImportDataPage] = React.useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = React.useState<boolean>(true);
  const showSuccessToast = useSuccessRegisterToast();
  const showErrorSavingToast = useErrorSavingDataToast();
  const [translatedData, setTranslatedData] = React.useState<{
    nombres: { [key: string]: string };
    estados: { [key: string]: string };
    tiposReglas: { [key: string]: string };
    reglas: { [key: string]: string };
  }>({
    nombres: {},
    estados: {},
    tiposReglas: {},
    reglas: {},
  });

  const formRef = React.useRef<FormInstance>(null);
  const [getVariants, { data, isLoading }] = useGetPeriodVariantsMutation();
  const [getVariables, { data: variables, isLoading: isLoadingVariables }] =
    useGetRulesVariablesMutation();
  const [getVariantById, { data: variantById }] = useGetPeriodVariantByIdMutation();
  const [getRules, { data: rulesData }] = useGetRulesMutation();
  const [
    createVariant,
    { isLoading: isLoadingCreate, isSuccess: isSuccessCreate, isError: isErrorCreate },
  ] = useCreatePeriodVariantMutation();
  const [
    updateVariant,
    { isLoading: isLoadingUpdate, isSuccess: isSuccessUpdate, isError: isErrorUpdate },
  ] = useUpdatePeriodVariantMutation();
  const [
    updateVariantState,
    { isLoading: isLoadingState, isSuccess: isSuccessState, isError: isErrorState },
  ] = useUpdatePeriodVariantStateMutation();
  const [getRuleTypes, { data: ruleTypes, isLoading: isLoadingRuleTypes }] =
    useGetRuleTypesMutation();

  const tiposReglasOptions = React.useMemo(() => {
    return (ruleTypes?.data ?? []).map((rt: any) => ({
      label: translatedData.tiposReglas[rt.reglaTipoNombre] || rt.reglaTipoNombre,
      value: rt.reglaTipoId,
    }));
  }, [ruleTypes?.data, translatedData.tiposReglas]);

  const tipoReglaIdForm = Form.useWatch("reglaTipoId", form);

  useEffect(() => {
    getRuleTypes();
  }, [getRuleTypes]);

  useEffect(() => {
    if (periodo.periodoId !== 0) {
      getRules(periodo.periodoId);
    }
  }, [getRules, periodo]);

  // Efecto para traducir los campos de la tabla (nombre y estadoNombre)
  useEffect(() => {
    const translateTableData = async () => {
      const hasData = data?.data && Array.isArray(data.data) && data.data.length > 0;

      if (!hasData) {
        setTranslatedData((prev) => ({
          ...prev,
          nombres: {},
          estados: {},
        }));
        return;
      }

      const nombresTranslations: { [key: string]: string | null } = {};
      const estadosTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      data.data.forEach((record: IReglasVariantes) => {
        // Traducir nombre
        const nombre = (record as any).nombre || record.Nombre;
        if (nombre && !nombresTranslations.hasOwnProperty(nombre)) {
          nombresTranslations[nombre] = null;
          translationPromises.push(
            _translateValue(SCREEN_NAME, nombre, locale).then((translated) => {
              nombresTranslations[nombre] = translated;
            })
          );
        }

        // Traducir estado
        if (record.estadoNombre && !estadosTranslations.hasOwnProperty(record.estadoNombre)) {
          estadosTranslations[record.estadoNombre] = null;
          translationPromises.push(
            _translateValue(SCREEN_NAME, record.estadoNombre, locale).then((translated) => {
              estadosTranslations[record.estadoNombre] = translated;
            })
          );
        }
      });

      await Promise.all(translationPromises);

      setTranslatedData((prev) => ({
        ...prev,
        nombres: nombresTranslations as { [key: string]: string },
        estados: estadosTranslations as { [key: string]: string },
      }));
    };

    translateTableData();
  }, [data?.data, locale]);

  // Efecto para traducir los tipos de reglas
  useEffect(() => {
    const translateRuleTypes = async () => {
      const hasData = ruleTypes?.data && Array.isArray(ruleTypes.data) && ruleTypes.data.length > 0;

      if (!hasData) {
        setTranslatedData((prev) => ({
          ...prev,
          tiposReglas: {},
        }));
        return;
      }

      const tiposReglasTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      ruleTypes.data.forEach((rt: any) => {
        if (rt.reglaTipoNombre && !tiposReglasTranslations.hasOwnProperty(rt.reglaTipoNombre)) {
          tiposReglasTranslations[rt.reglaTipoNombre] = null;
          translationPromises.push(
            _translateValue(SCREEN_NAME, rt.reglaTipoNombre, locale).then((translated) => {
              tiposReglasTranslations[rt.reglaTipoNombre] = translated;
            })
          );
        }
      });

      await Promise.all(translationPromises);

      setTranslatedData((prev) => ({
        ...prev,
        tiposReglas: tiposReglasTranslations as { [key: string]: string },
      }));
    };

    translateRuleTypes();
  }, [ruleTypes?.data, locale]);

  // Efecto para traducir las descripciones de las reglas
  useEffect(() => {
    const translateRules = async () => {
      const hasColones = rulesData?.data?.colones && Array.isArray(rulesData.data.colones) && rulesData.data.colones.length > 0;
      const hasDolares = rulesData?.data?.dolares && Array.isArray(rulesData.data.dolares) && rulesData.data.dolares.length > 0;

      if (!hasColones && !hasDolares) {
        setTranslatedData((prev) => ({
          ...prev,
          reglas: {},
        }));
        return;
      }

      const reglasTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      if (hasColones) {
        rulesData.data.colones.forEach((ru: any) => {
          if (ru.descripcion && !reglasTranslations.hasOwnProperty(ru.descripcion)) {
            reglasTranslations[ru.descripcion] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, ru.descripcion, locale).then((translated) => {
                reglasTranslations[ru.descripcion] = translated;
              })
            );
          }
        });
      }

      if (hasDolares) {
        rulesData.data.dolares.forEach((ru: any) => {
          if (ru.descripcion && !reglasTranslations.hasOwnProperty(ru.descripcion)) {
            reglasTranslations[ru.descripcion] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, ru.descripcion, locale).then((translated) => {
                reglasTranslations[ru.descripcion] = translated;
              })
            );
          }
        });
      }

      await Promise.all(translationPromises);

      setTranslatedData((prev) => ({
        ...prev,
        reglas: reglasTranslations as { [key: string]: string },
      }));
    };

    translateRules();
  }, [rulesData?.data?.colones, rulesData?.data?.dolares, locale]);

  useEffect(() => {
    getVariables();
  }, [getVariables]);

  useEffect(() => {
    getVariants(periodo.procesoId);
  }, [
    getVariants,
    isSuccessCreate,
    isSuccessUpdate,
    isErrorCreate,
    isErrorUpdate,
    periodo,
    isSuccessState,
    isErrorState,
  ]);

  useEffect(() => {
    if (isErrorUpdate) showErrorSavingToast();

    if (isSuccessUpdate) {
      showSuccessToast();
      form.resetFields();
      setVarianteId(0);
    }
    setDrawerOpen(false);
  }, [isSuccessUpdate, form, isErrorUpdate, showSuccessToast, showErrorSavingToast]);

  useEffect(() => {
    if (isErrorCreate) showErrorSavingToast();

    if (isSuccessCreate) {
      showSuccessToast();
      form.resetFields();
      setVarianteId(0);
    }
    setDrawerOpen(false);
  }, [isSuccessCreate, form, isErrorCreate, showSuccessToast, showErrorSavingToast]);

  const onClickEditarVariante = React.useCallback(
    (record: IReglasVariantes) => {
      setVarianteId(record.reglaId);
      getVariantById(record.reglaId).then((response: { data: { data: IReglasVariantes } }) => {
        form.setFieldsValue(response?.data?.data);
        setDrawerOpen(true);
      });
    },
    [form, getVariantById],
  );

  const cambiarEstadoVariante = React.useCallback(
    (value: number, id: number) => {
      updateVariantState({ id, value: value === 11 ? false : true });
    },
    [updateVariantState],
  );

  const submit = (values: IReglasVariantes) => {
    if (varianteId !== 0) {
      updateVariant({
        ...values,
        id: varianteId,
      });
    } else {
      createVariant({ ...values, procesoId: periodo.procesoId });
    }
  };

  const handleOkVariables = () => {
    if (variableSelected !== null) {
      const newConditionText = `${form.getFieldValue("condicionCodigo") ?? ""}@${variableSelected}`;
      form.setFieldValue("condicionCodigo", newConditionText);
    }

    setVariableSelected(null);
    setModalvariablesOpen(false);
  };

  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getImportDataPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setImportDataPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          setImportDataPage(null);
          setIsLoadingContent(false);
        }
      });
    return () => {
      cancel = true;
    };
  }, [locale]);

  const columns = React.useMemo<ColumnsType<IReglasVariantes>>(
    () => [
      {
        title: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.rules_table?.[0]?.label || "Nombre de la regla"),
        dataIndex: "nombre",
        render: (text: string) => translatedData.nombres[text] || text,
      },
      {
        title: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.rules_table?.[1]?.label || "Prioridad"),
        dataIndex: "prioridad",
      },
      {
        title: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.rules_table?.[2]?.label || "Estado"),
        dataIndex: "estadoNombre",
        render: (text: string) => translatedData.estados[text] || text,
      },
      {
        title: "",
        dataIndex: "actions",
        fixed: "right",
        width: 100,
        render: (_, record) => (
          <Dropdown
            overlayClassName="table-dropdown"
            className="cursor-pointer"
            key={record.reglaId}
            trigger={["click"]}
            align={{ offset: [-40, 4] }}
            menu={{
              items: [
                {
                  key: "1",
                  label: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (importDataPage?.select_Periods.edit_button || "Editar"),
                  icon: <Icon name="" color="#A7A7A7" />,
                  onClick: () => onClickEditarVariante(record),
                },
                {
                  key: "2",
                  label: record.estadoId === 11 ? isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (importDataPage?.select_Periods.inactive_button || "Inactivar") : isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (importDataPage?.select_Periods.active_button || "Activar"),
                  icon: <Icon name={record.estadoId === 11 ? "" : ""} color="#A7A7A7" />,
                  onClick: () => cambiarEstadoVariante(record.estadoId, record.reglaId),
                },
              ],
            }}
          >
            <Icon name="" isTouchable />
          </Dropdown>
        ),
      },
    ],
    [onClickEditarVariante, cambiarEstadoVariante, isLoadingContent, importDataPage?.select_Periods, importDataPage?.rules_table, translatedData],
  );

  return (
    <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
      <Content style={{ marginTop: "16px" }}>
        <SectionHeaderStyled>
          <MediumLargeHeadingStyled>
            {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 300 }} /> : (importDataPage?.select_Periods.subtitleTR || "Variantes de reglas de estimación para el periodo")} {periodo.periodoNombre}
          </MediumLargeHeadingStyled>
          <Flex style={{ gap: "15px" }}>
            <Button onClick={goBack}>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (importDataPage?.select_Periods.before_button || "Ir atrás")}</Button>
            <Button
              type="primary"
              onClick={() => {
                form.resetFields();
                setDrawerOpen(true);
              }}
            >
              {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (importDataPage?.select_Periods.new_variant_button || "Nueva variante")}
            </Button>
          </Flex>
        </SectionHeaderStyled>
        <Card bodyStyle={{ padding: 0, width: "100%" }}>
          <Table<IReglasVariantes>
            loading={isLoading || isLoadingCreate || isLoadingUpdate || isLoadingState}
            columns={columns}
            dataSource={data?.data?.reglas}
            rowKey={(v) => v.reglaId}
            pagination={{
              defaultPageSize: 5,
              showSizeChanger: true,
              style: { marginRight: "30px" },
            }}
            scroll={{ x: 800 }}
          />
          <Drawer
            size="large"
            placement="right"
            title={
              varianteId !== 0
                ? isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 300 }} /> : `${importDataPage?.select_Periods.subtitleFV || "Edición de la regla"} ${variantById?.data?.nombre}`
                : isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 300 }} /> : (importDataPage?.select_Periods.subtitleF || "Nueva variante de reglas de estimación")
            }
            open={drawerOpen}
            onClose={() => {
              setVarianteId(0);
              setDrawerOpen(false);
            }}
          >
            <Form
              form={form}
              layout="vertical"
              ref={formRef}
              onFinish={submit}
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
              initialValues={{
                id: 1,
              }}
            >
              <Form.Item
                name="nombre"
                label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods.rule_name || "Nombre de la regla")}
                required
                rules={[
                  {
                    required: true,
                    message: importDataPage?.select_Periods?.required_message || "Este campo es requerido.",
                  },
                ]}
                style={{ marginBottom: 4 }}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="condicionCodigo"
                label={
                  <Flex style={{ gap: "10px", alignItems: "center" }}>
                    <label htmlFor="condicionCodigo">{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods.rule_conditions || "Condiciones de la regla (SQL)")}</label>
                    <Button size="small" onClick={() => setModalvariablesOpen(true)}>
                      {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (importDataPage?.select_Periods.insert_button || "Insertar variable")}
                    </Button>
                  </Flex>
                }
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: importDataPage?.select_Periods?.required_message || "Este campo es requerido",
                  },
                ]}
              >
                {drawerOpen && (
                  <ConfigurationInput
                    data={{
                      language: "sql",
                      id: 1,
                      label: "",
                      nombre: "condicionCodigo",
                      tipo: "CodeEditor",
                      valor: form.getFieldValue("condicionCodigo"),
                      descripcion: importDataPage?.select_Periods?.rule_conditions_description || "Código SQL con las condiciones de la regla de estimación",
                    }}
                    onChange={(value) => form.setFieldValue("condicionCodigo", value ?? null)}
                  />
                )}
              </Form.Item>
              <Form.Item
                name="reglaTipoId"
                label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods.rule_type || "Tipo de regla")}
                rules={[
                  {
                    required: true,
                    message: importDataPage?.select_Periods?.required_message || "Este campo es requerido",
                  },
                ]}
              >
                <Select
                  allowClear
                  placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods.select_option || "Seleccionar tipo de regla")}
                  size="large"
                  loading={isLoadingRuleTypes}
                  options={tiposReglasOptions}
                />
              </Form.Item>
              <Form.Item
                name="prioridad"
                label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods.priority || "Prioridad de la variante")}
                rules={[
                  {
                    required: true,
                    message: importDataPage?.select_Periods?.required_message || "Este campo es requerido",
                  },
                ]}
              >
                <InputNumber min={1} controls={false} />
              </Form.Item>

              <Collapse
                items={[
                  {
                    key: 1,
                    label: (
                      <SmallHeadingStyled style={{ margin: 0 }}>
                        {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods.estimation_rules || "Reglas de estimación")}
                      </SmallHeadingStyled>
                    ),
                    children: (
                      <div>
                        {" "}
                        <Form.Item
                          name="reglaContado"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods.rule_one || "Pago único (colones)")}
                          required
                          rules={[
                            {
                              required: true,
                              message: importDataPage?.select_Periods?.required_message || "Este campo es requerido.",
                            },
                          ]}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods.select_option || "Seleccionar regla")}
                            size="large"
                            options={rulesData?.data?.colones?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="reglaContadoAmazon"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods.rule_two || "Pago único con Amazon (colones)")}
                          required
                          rules={[
                            {
                              required: true,
                              message: importDataPage?.select_Periods?.required_message || "Este campo es requerido.",
                            },
                          ]}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods.select_option || "Seleccionar regla")}
                            size="large"
                            options={rulesData?.data?.colones?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="reglaCuotas"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods.rule_three || "Pago a cuotas (colones)")}
                          style={{ marginBottom: 4 }}
                          required={tipoReglaIdForm !== 1}
                          rules={[
                            {
                              required: tipoReglaIdForm !== 1,
                              message: importDataPage?.select_Periods?.required_message || "Este campo es requerido.",
                            },
                          ]}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods.select_option || "Seleccionar regla")}
                            size="large"
                            options={rulesData?.data?.colones?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="reglaCuotasAmazon"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods.rule_four || "Pago a cuotas con Amazon (colones)")}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods.select_option || "Seleccionar regla")}
                            size="large"
                            options={rulesData?.data?.colones?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="reglaContadoDolares"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods.rule_five || "Pago único (dólares)")}
                          required
                          rules={[
                            {
                              required: true,
                              message: importDataPage?.select_Periods?.required_message || "Este campo es requerido.",
                            },
                          ]}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods.select_option || "Seleccionar regla")}
                            size="large"
                            options={rulesData?.data?.dolares?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="reglaContadoAmazonDolares"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods.rule_six || "Pago único con Amazon (dólares)")}
                          required
                          rules={[
                            {
                              required: true,
                              message: importDataPage?.select_Periods?.required_message || "Este campo es requerido.",
                            },
                          ]}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods.select_option || "Seleccionar regla")}
                            size="large"
                            options={rulesData?.data?.dolares?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="reglaCuotasDolares"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods.rule_seven || "Pago a cuotas (dólares)")} 
                          style={{ marginBottom: 4 }}
                          required={tipoReglaIdForm !== 1}
                          rules={[
                            {
                              required: tipoReglaIdForm !== 1,
                              message: importDataPage?.select_Periods?.required_message || "Este campo es requerido.",
                            },
                          ]}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods.select_option || "Seleccionar regla")}
                            size="large"
                            options={rulesData?.data?.dolares?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="reglaCuotasAmazonDolares"
                          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (importDataPage?.select_Periods.rule_eight || "Pago a cuotas con Amazon (dólares)")}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            allowClear
                            placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (importDataPage?.select_Periods.select_option || "Seleccionar regla")}
                            size="large"
                            options={rulesData?.data?.dolares?.map((ru: any) => ({
                              label: translatedData.reglas[ru.descripcion] || ru.descripcion,
                              value: ru.id,
                            }))}
                          />
                        </Form.Item>
                      </div>
                    ),
                  },
                ]}
                defaultActiveKey={1}
                ghost
                style={{ background: "transparent" }}
              />

              <Button
                type="primary"
                size="large"
                htmlType="submit"
                style={{ alignSelf: "flex-end" }}
                loading={isLoadingUpdate || isLoadingCreate}
              >
                {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (importDataPage?.select_Periods.save_button || "Guardar")}
              </Button>
            </Form>
            <Modal
              closable={false}
              open={modalVariablesOpen}
              onOk={handleOkVariables}
              okText={importDataPage?.select_Periods?.insert_button || "Insertar"}
              onCancel={() => setModalvariablesOpen(false)}
              cancelText={importDataPage?.select_Periods?.cancel_button || "Cancelar"}
              style={{ maxWidth: 450 }}
            >
              <Form.Item label={importDataPage?.select_Periods?.variable_select_label || "Variable a insertar"}>
                <Select
                  loading={isLoadingVariables}
                  onChange={(e) => setVariableSelected(e)}
                  options={variables?.data?.map((va: string) => ({
                    label: va,
                    value: va,
                  }))}
                  allowClear
                  value={variableSelected ?? ""}
                  placeholder={importDataPage?.select_Periods?.variable_select_placeholder || "Seleccione una variable"}
                />
              </Form.Item>
            </Modal>
          </Drawer>
        </Card>
      </Content>
    </Flex>
  );
};

export default PeriodEstimationVariants;
